import React from 'react'
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Button,
  Typography,
  Stack,
  Chip,
  Autocomplete,
} from '@mui/material'
import {
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material'

// Condition field definitions with their valid operators and value types
const CONDITION_FIELDS = [
  {
    value: 'time_of_day',
    label: 'Time of Day',
    operators: ['eq', 'in'],
    valueType: 'select',
    options: ['morning', 'afternoon', 'evening', 'night'],
  },
  {
    value: 'day_of_week',
    label: 'Day of Week',
    operators: ['eq', 'in'],
    valueType: 'multiselect',
    options: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
  },
  {
    value: 'intervention_day',
    label: 'Intervention Day',
    operators: ['eq', 'lt', 'lte', 'gt', 'gte'],
    valueType: 'number',
    description: 'Days since intervention started',
  },
  {
    value: 'intervention_week',
    label: 'Intervention Week',
    operators: ['eq', 'lt', 'lte', 'gt', 'gte'],
    valueType: 'number',
    description: 'Weeks since intervention started',
  },
  {
    value: 'intervention_phase',
    label: 'Intervention Phase',
    operators: ['eq', 'in'],
    valueType: 'select',
    options: ['onboarding', 'active', 'maintenance', 'completion'],
  },
  {
    value: 'engagement_tier',
    label: 'Engagement Tier',
    operators: ['eq', 'in'],
    valueType: 'multiselect',
    options: ['at_risk', 'low', 'moderate', 'engaged', 'highly_engaged'],
  },
  {
    value: 'engagement_score',
    label: 'Engagement Score',
    operators: ['eq', 'lt', 'lte', 'gt', 'gte'],
    valueType: 'number',
    description: '0-100 score',
  },
  {
    value: 'times_completed',
    label: 'Times Completed',
    operators: ['eq', 'lt', 'lte', 'gt', 'gte'],
    valueType: 'number',
    description: 'Number of times this track completed',
  },
  {
    value: 'days_since_last_session',
    label: 'Days Since Last Session',
    operators: ['eq', 'lt', 'lte', 'gt', 'gte'],
    valueType: 'number',
  },
  {
    value: 'current_streak',
    label: 'Current Streak',
    operators: ['eq', 'lt', 'lte', 'gt', 'gte'],
    valueType: 'number',
    description: 'Consecutive days of engagement',
  },
  {
    value: 'completion_percentage',
    label: 'Completion Percentage',
    operators: ['eq', 'lt', 'lte', 'gt', 'gte'],
    valueType: 'number',
    description: '0-100 percentage',
  },
  {
    value: 'risk_factors',
    label: 'Risk Factors',
    operators: ['contains', 'not_contains'],
    valueType: 'text',
    description: 'Risk code to check for',
  },
  {
    value: 'goal_status',
    label: 'Goal Status',
    operators: ['eq', 'in'],
    valueType: 'select',
    options: ['not_started', 'in_progress', 'achieved', 'abandoned'],
  },
]

const OPERATORS = {
  eq: { label: 'equals', symbol: '=' },
  lt: { label: 'less than', symbol: '<' },
  lte: { label: 'less than or equal', symbol: '<=' },
  gt: { label: 'greater than', symbol: '>' },
  gte: { label: 'greater than or equal', symbol: '>=' },
  in: { label: 'is one of', symbol: 'in' },
  contains: { label: 'contains', symbol: 'has' },
  not_contains: { label: 'does not contain', symbol: '!has' },
}

function ConditionRow({ condition, index, onChange, onRemove, canRemove }) {
  const fieldDef = CONDITION_FIELDS.find((f) => f.value === condition.field) || {}
  const validOperators = fieldDef.operators || ['eq']

  const handleFieldChange = (newField) => {
    const newFieldDef = CONDITION_FIELDS.find((f) => f.value === newField) || {}
    const defaultOperator = newFieldDef.operators?.[0] || 'eq'
    const defaultValue = newFieldDef.valueType === 'multiselect' ? [] : ''
    onChange(index, { field: newField, op: defaultOperator, value: defaultValue })
  }

  const handleOperatorChange = (newOp) => {
    // Reset value to array if switching to 'in' operator
    const newValue = newOp === 'in' && !Array.isArray(condition.value) ? [] : condition.value
    onChange(index, { ...condition, op: newOp, value: newValue })
  }

  const renderValueInput = () => {
    const { valueType, options } = fieldDef

    // For 'in' operator, always use multiselect
    if (condition.op === 'in' && options) {
      return (
        <Autocomplete
          multiple
          size="small"
          options={options}
          value={Array.isArray(condition.value) ? condition.value : []}
          onChange={(e, newValue) => onChange(index, { ...condition, value: newValue })}
          renderInput={(params) => <TextField {...params} placeholder="Select values" />}
          renderTags={(value, getTagProps) =>
            value.map((option, i) => (
              <Chip size="small" label={option} {...getTagProps({ index: i })} key={option} />
            ))
          }
          sx={{ minWidth: 200 }}
        />
      )
    }

    switch (valueType) {
      case 'select':
        return (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={condition.value || ''}
              onChange={(e) => onChange(index, { ...condition, value: e.target.value })}
            >
              {(options || []).map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )

      case 'multiselect':
        return (
          <Autocomplete
            multiple
            size="small"
            options={options || []}
            value={Array.isArray(condition.value) ? condition.value : []}
            onChange={(e, newValue) => onChange(index, { ...condition, value: newValue })}
            renderInput={(params) => <TextField {...params} placeholder="Select values" />}
            renderTags={(value, getTagProps) =>
              value.map((option, i) => (
                <Chip size="small" label={option} {...getTagProps({ index: i })} key={option} />
              ))
            }
            sx={{ minWidth: 200 }}
          />
        )

      case 'number':
        return (
          <TextField
            size="small"
            type="number"
            value={condition.value || ''}
            onChange={(e) => onChange(index, { ...condition, value: parseInt(e.target.value) || 0 })}
            sx={{ width: 100 }}
          />
        )

      default:
        return (
          <TextField
            size="small"
            value={condition.value || ''}
            onChange={(e) => onChange(index, { ...condition, value: e.target.value })}
            placeholder="Value"
            sx={{ minWidth: 150 }}
          />
        )
    }
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50 }}>
        {index === 0 ? 'When' : 'AND'}
      </Typography>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <Select
          value={condition.field || ''}
          onChange={(e) => handleFieldChange(e.target.value)}
          displayEmpty
        >
          <MenuItem value="" disabled>
            Select field
          </MenuItem>
          {CONDITION_FIELDS.map((field) => (
            <MenuItem key={field.value} value={field.value}>
              {field.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select
          value={condition.op || 'eq'}
          onChange={(e) => handleOperatorChange(e.target.value)}
          disabled={!condition.field}
        >
          {validOperators.map((op) => (
            <MenuItem key={op} value={op}>
              {OPERATORS[op]?.label || op}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {renderValueInput()}

      <IconButton
        size="small"
        onClick={() => onRemove(index)}
        disabled={!canRemove}
        color="error"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  )
}

function ConditionalBlockEditor({ block, onChange, onRemove }) {
  const conditions = block.conditions || [{ field: '', op: 'eq', value: '' }]

  const handleConditionChange = (index, updatedCondition) => {
    const newConditions = [...conditions]
    newConditions[index] = updatedCondition
    onChange({ ...block, conditions: newConditions })
  }

  const handleConditionRemove = (index) => {
    const newConditions = conditions.filter((_, i) => i !== index)
    onChange({ ...block, conditions: newConditions.length > 0 ? newConditions : [{ field: '', op: 'eq', value: '' }] })
  }

  const addCondition = () => {
    onChange({
      ...block,
      conditions: [...conditions, { field: '', op: 'eq', value: '' }],
    })
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
      {/* Block Header */}
      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
        <TextField
          label="Block Name"
          value={block.name || ''}
          onChange={(e) => onChange({ ...block, name: e.target.value })}
          size="small"
          placeholder="e.g., Morning session, High engagement"
          sx={{ width: 300 }}
        />
        <IconButton size="small" onClick={onRemove} color="error">
          <DeleteIcon />
        </IconButton>
      </Box>

      {/* Conditions */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Conditions (all must match)
        </Typography>
        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
          {conditions.map((condition, index) => (
            <ConditionRow
              key={index}
              condition={condition}
              index={index}
              onChange={handleConditionChange}
              onRemove={handleConditionRemove}
              canRemove={conditions.length > 1}
            />
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addCondition}
            sx={{ mt: 1 }}
          >
            Add Condition
          </Button>
        </Box>
      </Box>

      {/* Extended Instructions */}
      <Typography variant="subtitle2" gutterBottom>
        Additional Instructions (when conditions match)
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={4}
        value={block.extend?.context_preamble || block.instructions || ''}
        onChange={(e) =>
          onChange({
            ...block,
            extend: { ...block.extend, context_preamble: e.target.value },
            instructions: e.target.value,
          })
        }
        placeholder="Enter additional instructions to apply when all conditions are met..."
        size="small"
      />

      {/* Preview */}
      {conditions.some((c) => c.field) && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1 }}>
          <Typography variant="caption" color="primary.main" fontWeight="medium">
            Rule Preview:
          </Typography>
          <Typography variant="body2" color="primary.main">
            {conditions
              .filter((c) => c.field)
              .map((c, i) => {
                const fieldDef = CONDITION_FIELDS.find((f) => f.value === c.field)
                const fieldLabel = fieldDef?.label || c.field
                const opLabel = OPERATORS[c.op]?.symbol || c.op
                const valueStr = Array.isArray(c.value) ? c.value.join(', ') : c.value
                return `${i > 0 ? ' AND ' : ''}${fieldLabel} ${opLabel} ${valueStr}`
              })
              .join('')}
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

export default ConditionalBlockEditor
